/**
 * The pure reading of a word grid, mirrored from the server's `groups.py` (ADR 0059): how two
 * words are compared, and which groups a word order gives away. The shared fixtures keep both
 * in step. The verdict itself (found, one away) is the server's alone: it needs the key.
 */
import type { WordGroup } from "./types";

/** Mirrors `fold`: no case, no accent, no surrounding or doubled space. */
export function foldWord(word: string): string {
  return word
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .normalize("NFC")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .split(/\s+/u)
    .filter(Boolean)
    .join(" ");
}

/** Whether two proposals are the same set of words. */
export function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const folded = new Set(a.map(foldWord));
  return b.every((word) => folded.has(foldWord(word)));
}

/**
 * Mirrors `revealing_groups`: the groups a word order gives away, laid out as one run or down
 * one column of the grid (tiles are shown `groupSize` to a row, in definition order).
 */
export function revealingGroups(words: string[], groups: WordGroup[], groupSize: number): number[] {
  const position = new Map(words.map((word, index) => [foldWord(word), index] as const));
  const revealing: number[] = [];
  groups.forEach((group, index) => {
    const places = group.words
      .map((word) => position.get(foldWord(word)))
      .filter((p): p is number => p !== undefined)
      .sort((a, b) => a - b);
    if (places.length !== groupSize || groupSize < 2) return;
    const run = places[places.length - 1] - places[0] === groupSize - 1;
    const column = new Set(places.map((p) => p % groupSize)).size === 1;
    if (run || column) revealing.push(index);
  });
  return revealing;
}

/** A small seeded generator (mulberry32): the editor's shuffles are reproducible. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffled<T>(items: readonly T[], random: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The words of a key laid out in an order that gives no group away: shuffled with the seed,
 * reshuffled (from the same generator) until no group sits in one run or one column. Gives up
 * after a few hundred tries on a board too small to hide anything, and returns the last one.
 */
export function layoutFromGroups(groups: WordGroup[], groupSize: number, seed: number): string[] {
  const words = groups.flatMap((group) => group.words.map((word) => word.trim()));
  const random = seededRandom(seed);
  let order = shuffled(words, random);
  for (
    let tries = 0;
    tries < 500 && revealingGroups(order, groups, groupSize).length > 0;
    tries++
  ) {
    order = shuffled(words, random);
  }
  return order;
}
